// lib imports
import * as React from "react";
import { Grid, Link, Stack, TextField } from "@mui/material";

import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import Pagination from "@mui/material/Pagination";

// component imports
import ImmunizationDefer from "../modals/ImmunizationDefer";
import ImmunizationGiven from "../modals/ImmunizationGiven";
import FilterMenu from "./FilterMenu";

// helpers and setting imports
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import { ImagesContext } from "../Context";
import debounce from "lodash/debounce";
import BadgeList from "../common/BadgeList";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import LoadingTable from "../shared/LoadingTable";
import {
  formatToUsDateFromUTC,
  formatToUsDateFromUTCNew,
} from "../utils/DateHelper";

interface Props {}

interface ChipData {
  key: number;
  label: string;
  type: string;
}

const Immunization: React.FC<Props> = (props: any) => {
  // authenticationContext and chat context and other contexts
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext(ImagesContext);
  const afterFetch = React.useRef(false);

  // pagination states
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [pageLinks, setPageLinks] = React.useState<any>([]);
  const perPage = 10;

  // other states
  const [imTypeOptions, setImTypeOptions] = React.useState<any>([]);
  const [insuranceTypeOptions, setInsuranceTypeOptions] = React.useState<any>(
    []
  );
  const [insurancePlanNameOptions, setInsurancePlanNameOptions] =
    React.useState<string[]>([]);
  const [programNames, setProgramNames] = React.useState<[]>([]);
  const [patientLists, setPatientLists] = React.useState<any>([]);
  const [lastName, setLastName] = React.useState<string>("");
  const [dob, setDob] = React.useState<string>("");
  const [dueDateFrom, setDueDateFrom] = React.useState<string>("");

  // we want to see t+30 days data
  const defaultThroughDate = () => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 90);
    const defaultDateString = defaultDate.toISOString().split("T")[0];
    return defaultDateString;
  };
  const [dueDateThrough, setDueDateThrough] =
    React.useState<string>(defaultThroughDate);
  const [immunizations, setImmunizations] = React.useState<any>([]);
  const [immunizationsCount, setImmunizationsCount] = React.useState<any>(0);
  const [paginatedList, setPaginatedList] = React.useState<any>([]);
  const [sortObject, setSortObject] = React.useState<any>({
    field: "patients.last_name",
    direction: "desc",
  });
  const [deferModalOpen, setDeferModalOpen] = React.useState<boolean>(false);
  const [givenModalOpen, setGivenModalOpen] = React.useState<boolean>(false);
  const [deferSelectionImId, setDeferSelectionImId] =
    React.useState<string>("");
  const [givenSelectionImId, setGivenSelectionImId] =
    React.useState<string>("");
  const [renderingKey, setRenderingKey] = React.useState<number>(Math.random);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [checkedImTypes, setCheckedImTypes] = React.useState<any>([]);
  const [checkedInsuranceTypes, setCheckedInsuranceTypes] = React.useState<any>(
    []
  );
  const [checkedInsurancePlanNames, setCheckedInsurancePlanNames] =
    React.useState<any[]>([]);
  const [checkedProgramNames, setCheckedProgramNames] = React.useState<any>([]);
  const [checkedPatientList, setCheckedPatientList] = React.useState<any>("");
  const [patientAgeRangeSelected, setPatientAgeRangeSelected] =
    React.useState<boolean>(false);
  const [
    showOnlyPatientsInLivingFacility,
    setShowOnlyPatientsInLivingFacility,
  ] = React.useState<boolean>(false);
  const [minAge, setMinAge] = React.useState<string>("");
  const [maxAge, setMaxAge] = React.useState<string>("");
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const imTypesQuery = checkedImTypes
    .map((item) => `im_types[]=${item}`)
    .join("&");
  const insuranceTypesQuery = checkedInsuranceTypes
    .map((item) => `insurance_types[]=${item}`)
    .join("&");
  const insurancePlanNamesQuery = checkedInsurancePlanNames
    .map((item) => `insurance_plan_names[]=${item}`)
    .join("&");
  const programNamesQuery = checkedProgramNames
    .map((item) => `program_names[]=${item}`)
    .join("&");
  const ageQueryParts = [] as string[];
  if (patientAgeRangeSelected) {
    if (minAge) ageQueryParts.push(`min_age=${encodeURIComponent(minAge)}`);
    if (maxAge) ageQueryParts.push(`max_age=${encodeURIComponent(maxAge)}`);
  }
  const ageQuery = ageQueryParts.join("&");
  const livingFacilityQuery = showOnlyPatientsInLivingFacility
    ? "living_facility_only=true"
    : "";

  const calendarRef = React.useRef(null);
  const hasMounted = React.useRef(false);
  const lastNameRef = React.useRef("");
  const timeoutRef = React.useRef(null);

  const clearFilter = () => {
    setDueDateThrough(defaultThroughDate);
    setDueDateFrom("");
    setDob("");
    setCheckedImTypes([]);
    setLastName("");
    lastNameRef.current = "";
    setCheckedPatientList("");
    setPatientAgeRangeSelected(false);
    setMinAge("");
    setMaxAge("");
    setCheckedInsuranceTypes([]);
    setCheckedInsurancePlanNames([]);
    setCheckedProgramNames([]);
    setShowOnlyPatientsInLivingFacility(false);
  };

  const handleOpenFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  //inital useEffect and data fetch
  React.useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      getForecast(true);
    }, 500);
    setSortObject({
      field: "patients.last_name",
      direction: "desc",
    });
  }, [
    renderingKey,
    dob,
    dueDateFrom,
    dueDateThrough,
    checkedImTypes,
    checkedPatientList,
    checkedInsuranceTypes,
    checkedInsurancePlanNames,
    checkedProgramNames,
    patientAgeRangeSelected,
    minAge,
    maxAge,
    showOnlyPatientsInLivingFacility,
  ]);

  // Pagination fetch
  React.useEffect(() => {
    if (!hasMounted.current) {
      return;
    }
    getForecast();
  }, [currentPage]);

  // Sort refresh
  React.useEffect(() => {
    if (!hasMounted.current) {
      return;
    }
    getForecast();
  }, [sortObject]);

  const getForecast = (resetPages = false) => {
    const loadingTimeout = setTimeout(() => {
      setLoading(true);
    }, 250);
    fetch(
      `/immunizations?${[
        imTypesQuery,
        insuranceTypesQuery,
        insurancePlanNamesQuery,
        programNamesQuery,
        ageQuery,
        livingFacilityQuery,
      ]
        .filter(Boolean)
        .join("&")}${
        imTypesQuery ||
        insuranceTypesQuery ||
        insurancePlanNamesQuery ||
        programNamesQuery ||
        ageQuery ||
        livingFacilityQuery
          ? "&"
          : ""
      }last_name=${
        lastNameRef.current
      }&patient_list=${checkedPatientList}&dob=${dob}&insurance_plan_names=${checkedInsurancePlanNames}&due_date_from=${dueDateFrom}&due_date_through=${dueDateThrough}&page=${
        resetPages ? "1" : currentPage
      }&sort_by=${
        resetPages ? "patients.last_name" : sortObject.field
      }&sort_direction=${resetPages ? "desc" : sortObject.direction}`,
      {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        console.log(result);
        if (result.success == false) {
          alert(result.error);
        } else {
          if (result?.resource) {
            setImmunizations(result?.resource?.data);
            setImmunizationsCount(result?.resource?.immunizations_count);
            setImTypeOptions(result?.resource?.im_types);
            setInsuranceTypeOptions(result?.resource?.insurance_types);
            setInsurancePlanNameOptions(result?.resource?.insurance_plan_names);
            setProgramNames(result?.resource?.programs);
            setPatientLists(result?.resource?.patient_lists);
            setPageCount(result?.resource?.total_pages);
            if (resetPages) {
              setCurrentPage(1);
            }
          } else {
            alert("Something is wrong forecast cannot be fetched");
          }
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        clearTimeout(loadingTimeout);
        setLoading(false);
        setTimeout(() => {
          hasMounted.current = true;
        }, 2000);
      });
  };

  const debouncedFetchData = debounce(() => {
    getForecast();
  }, 500);

  const setOrder = (fieldName) => {
    console.log("Setting sort order to ", fieldName);
    setSortOrder(fieldName, sortObject.direction == "asc" ? "desc" : "asc");
  };

  const renderSortIcon = (fieldName) => {
    return sortObject.field == fieldName ? (
      <span className="sortIndicator">
        {sortObject.direction == "asc" ? (
          <img
            src={images.sort_ascending_src}
            width="10"
            className="sort-icon"
            alt="Sort Asc"
          />
        ) : (
          <img
            src={images.sort_descending_src}
            width="10"
            className="sort-icon"
            alt="Sort Desc"
          />
        )}
      </span>
    ) : (
      <span className="sortIndicator">
        <img
          src={images.sort_plain_src}
          width="10"
          className="sort-icon"
          alt="Sort Plain"
        />
      </span>
    );
  };

  const setSortOrder = (sortBy, direction) => {
    let sort = {
      field: sortBy,
      direction: direction,
    };
    setSortObject(sort);
  };

  const handleDeferModalOpen = (imId: string) => {
    setDeferSelectionImId(imId);
  };

  React.useEffect(() => {
    if (deferSelectionImId) {
      setDeferModalOpen(true);
    }
  }, [deferSelectionImId]);

  React.useEffect(() => {
    if (deferModalOpen == false) {
      setDeferSelectionImId(null);
    }
  }, [deferModalOpen]);

  const handleGivenModalOpen = (imId: string) => {
    setGivenSelectionImId(imId);
  };

  React.useEffect(() => {
    if (givenSelectionImId) {
      setGivenModalOpen(true);
    }
  }, [givenSelectionImId]);

  React.useEffect(() => {
    if (givenModalOpen == false) {
      setGivenSelectionImId(null);
    }
  }, [givenModalOpen]);

  // ------
  // end of given and defer handler

  const handleDeleteBadge = (badgeToDelete) => {
    if (badgeToDelete?.type == "imType") {
      setCheckedImTypes((badges) =>
        badges.filter((badge) => badge !== badgeToDelete.label)
      );
    }

    if (badgeToDelete?.type == "insuranceType") {
      setCheckedInsuranceTypes((badges) =>
        badges.filter((badge) => badge !== badgeToDelete.label)
      );
    }

    if (badgeToDelete?.type == "insurancePlanName") {
      setCheckedInsurancePlanNames((badges) =>
        badges.filter((badge) => badge !== badgeToDelete.label)
      );
    }

    if (badgeToDelete?.type == "programName") {
      setCheckedProgramNames((badges) =>
        badges.filter((badge) => badge !== badgeToDelete.label)
      );
    }

    if (badgeToDelete?.type == "patientList") {
      setCheckedPatientList("");
    }

    if (badgeToDelete?.type == "ageRange") {
      setPatientAgeRangeSelected(false);
      setMinAge("");
      setMaxAge("");
    }

    if (badgeToDelete?.type == "livingFacility") {
      setShowOnlyPatientsInLivingFacility(false);
    }
  };

  React.useEffect(() => {
    const imTypeBadges = checkedImTypes.map((imType) => ({
      key: imType,
      label: imType,
      type: "imType",
    }));

    const insuranceTypeBadges = checkedInsuranceTypes.map((type) => ({
      key: type,
      label: type,
      type: "insuranceType",
    }));

    const insurancePlanNameBadges = checkedInsurancePlanNames.map((type) => ({
      key: type,
      label: type,
      type: "insurancePlanName",
    }));

    const programNameBadges = checkedProgramNames.map((type) => ({
      key: type,
      label: type,
      type: "programName",
    }));

    const patientListBadge = checkedPatientList
      ? {
          key: checkedPatientList,
          label: checkedPatientList,
          type: "patientList",
        }
      : null;

    const ageBadge = patientAgeRangeSelected
      ? {
          key: "ageRange",
          label: `Age ${minAge || "?"}-${maxAge || "?"}`,
          type: "ageRange",
        }
      : null;

    const livingFacilityBadge = showOnlyPatientsInLivingFacility
      ? {
          key: "livingFacility",
          label: "In Living Facility",
          type: "livingFacility",
        }
      : null;

    const updatedBadgeData = [
      ...imTypeBadges,
      ...insuranceTypeBadges,
      ...programNameBadges,
      patientListBadge,
      ageBadge,
      livingFacilityBadge,
      ...insurancePlanNameBadges,
    ].filter(Boolean);

    setBadgeData(updatedBadgeData);
  }, [
    checkedImTypes,
    checkedInsuranceTypes,
    checkedInsurancePlanNames,
    checkedProgramNames,
    checkedPatientList,
    patientAgeRangeSelected,
    minAge,
    maxAge,
    showOnlyPatientsInLivingFacility,
  ]);

  return (
    <div className="immunization-container">
      <div className="immunization-header-section">
        <p className="immunization-header-text">Immunization Alerts</p>
      </div>
      <Grid container className="panel-container" borderRadius={"4px"}>
        <Grid item xs={12}>
          <Grid container className="panel-show-container">
            <Grid container className="panel-information-container">
              <Grid container direction="row" className="admin-header">
                <Grid item xs={12} className="box-header">
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    paddingX={3}
                    paddingY={1}
                    alignItems={"center"}
                    display={"flex"}
                  >
                    <Grid
                      item
                      xs={3}
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                    >
                      <p className="immunization-result">
                        Immunization Results
                      </p>
                      {immunizations.length > 0 && (
                        <p className="immunization-count-container badge">
                          {immunizationsCount}
                        </p>
                      )}
                    </Grid>
                    <Grid
                      xs={10}
                      item
                      display={"flex"}
                      direction={"row"}
                      alignItems={"center"}
                      gap={2.5}
                    >
                      <TextField
                        id="last_name"
                        className="textInput plainLabel"
                        placeholder="Search Last Name"
                        value={lastName}
                        onChange={(event) => {
                          setLastName(event.target.value);
                          lastNameRef.current = event.target.value;
                          debouncedFetchData();
                          setSortObject({
                            field: "patients.last_name",
                            direction: "desc",
                          });
                        }}
                        variant="outlined"
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            borderRadius: "4px",
                            height: 42.25,
                            backgroundColor: "#EFE9E7",
                            width: "100%",
                          },
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              border: "none",
                            },
                            "&:hover fieldset": {
                              border: "none",
                            },
                            "&.Mui-focused fieldset": {
                              border: "none",
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#1E1E1E",
                            fontSize: 12,
                            fontWeight: 500,
                            "&::placeholder": {
                              color: "#868382",
                              fontSize: 12,
                              fontWeight: 500,
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      <div className="date-container">
                        <DatePickerWithLabel
                          date={dob}
                          setDate={setDob}
                          label="Date of Birth"
                          initialDate=""
                        />
                      </div>
                      <div className="date-container">
                        <DatePickerWithLabel
                          date={dueDateFrom}
                          setDate={setDueDateFrom}
                          label="Due Date From"
                          initialDate=""
                        />
                      </div>
                      <div className="date-container">
                        <DatePickerWithLabel
                          date={dueDateThrough}
                          setDate={setDueDateThrough}
                          label="Due Date Through"
                          initialDate=""
                        />
                      </div>
                      <div
                        className="advanced-search-container"
                        onClick={handleOpenFilter}
                      >
                        <FilterListIcon sx={{ width: 20, height: 20 }} />
                        <p className="advanced-search-text">Advanced Search</p>
                        {anchorEl ? (
                          <ArrowDropUp sx={{ width: 24, height: 24 }} />
                        ) : (
                          <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
                        )}
                      </div>
                      <FilterMenu
                        imTypes={imTypeOptions}
                        insuranceTypeOptions={insuranceTypeOptions}
                        insurancePlanNameOptions={insurancePlanNameOptions}
                        programNames={programNames}
                        anchorEl={anchorEl}
                        setAnchorEl={setAnchorEl}
                        tagListLength={imTypeOptions?.length}
                        checkedItems={checkedImTypes}
                        setCheckedItems={setCheckedImTypes}
                        checkedInsuranceItems={checkedInsuranceTypes}
                        setCheckedInsuranceItems={setCheckedInsuranceTypes}
                        checkedInsurancePlanNames={checkedInsurancePlanNames}
                        setCheckedInsurancePlanNames={
                          setCheckedInsurancePlanNames
                        }
                        checkedProgramItems={checkedProgramNames}
                        setCheckedProgramItems={setCheckedProgramNames}
                        patientAgeRangeSelected={patientAgeRangeSelected}
                        setPatientAgeRangeSelected={setPatientAgeRangeSelected}
                        showOnlyPatientsInLivingFacility={
                          showOnlyPatientsInLivingFacility
                        }
                        setShowOnlyPatientsInLivingFacility={
                          setShowOnlyPatientsInLivingFacility
                        }
                        minAge={minAge}
                        setMinAge={setMinAge}
                        maxAge={maxAge}
                        setMaxAge={setMaxAge}
                        patientLists={patientLists}
                        checkedPatientList={checkedPatientList}
                        setCheckedPatientList={setCheckedPatientList}
                        clearFilter={clearFilter}
                      />
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container className="general-patient-info-container">
                {badgeData?.length > 0 && (
                  <div className="badge-container">
                    <BadgeList
                      badgeData={badgeData}
                      setBadgeData={setBadgeData}
                      handleDeleteBadge={handleDeleteBadge}
                    />
                  </div>
                )}

                <Grid
                  item
                  xs={12}
                  style={{
                    backgroundColor: "white",
                    display: "flex",
                    maxHeight: "calc(100vh - 370px)",
                    width: "100%",
                    flexDirection: "column",
                  }}
                >
                  {loading ? (
                    <div style={{ marginTop: "24px" }}>
                      <LoadingTable />
                    </div>
                  ) : (
                    <>
                      <TableContainer sx={{ maxHeight: "100%" }}>
                        <Table stickyHeader className="no-border-table">
                          <TableHead className="table-header-box">
                            <TableRow>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setOrder("patients.last_name");
                                }}
                                style={{ cursor: "pointer" }}
                                className="table-header name-header"
                                sx={{ paddingLeft: "24px !important" }}
                                width="30%"
                              >
                                <div className="row">
                                  <p className="table-header">Patient Name</p>
                                  {renderSortIcon("patients.last_name")}
                                </div>
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setOrder("users.date_of_birth");
                                }}
                                style={{ cursor: "pointer" }}
                                className="nowrap-header"
                                width="15%"
                              >
                                <div className="row">
                                  <p className="table-header"> Date Of Birth</p>
                                  {renderSortIcon("users.date_of_birth")}
                                </div>
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setOrder("vaccine_type");
                                }}
                                style={{ cursor: "pointer" }}
                                className="nowrap-header"
                                width="30%"
                              >
                                <div className="row">
                                  <p className="table-header">Immunization</p>
                                  {renderSortIcon("vaccine_type")}
                                </div>
                              </TableCell>
                              <TableCell
                                align="left"
                                onClick={() => {
                                  setOrder("recommended_date");
                                }}
                                style={{ cursor: "pointer" }}
                                className="nowrap-header"
                                width="15%"
                              >
                                <div className="row">
                                  <p className="table-header">
                                    Recommended Date
                                  </p>
                                  {renderSortIcon("recommended_date")}
                                </div>
                              </TableCell>
                              <TableCell
                                align="center"
                                className="nowrap-header"
                                width="5%"
                              >
                                <p className="table-header">Given</p>
                              </TableCell>
                              <TableCell
                                align="center"
                                className="nowrap-header"
                                sx={{ paddingRight: "16px !important" }}
                                width="5%"
                              >
                                <p className="table-header">Defer</p>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {immunizations.length > 0 ? (
                              <>
                                {immunizations.map((immunization, index) => (
                                  <TableRow
                                    key={index}
                                    className={
                                      index % 2 == 0 ? "rowEven" : "row"
                                    }
                                  >
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                      sx={{ paddingLeft: "24px !important" }}
                                    >
                                      <Link
                                        className={"table-body-link"}
                                        href={`/patient_reports/${immunization?.patient?.id}/immunizations`}
                                      >
                                        {immunization?.patient?.name_reversed}
                                      </Link>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {formatToUsDateFromUTCNew(
                                          immunization?.patient?.date_of_birth
                                        )}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {immunization?.vaccine_type}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="left"
                                      className="tableCell"
                                    >
                                      <p className="table-body-text">
                                        {immunization.recommended_date
                                          ? formatToUsDateFromUTC(
                                              immunization.recommended_date
                                            )
                                          : "N/A"}
                                      </p>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="center"
                                      className="tableCell"
                                    >
                                      <Link
                                        onClick={() =>
                                          handleGivenModalOpen(immunization.id)
                                        }
                                      >
                                        <img
                                          src={images.immunization_icon}
                                          alt="ImmunizationIcon"
                                          className="action-icon-image"
                                          style={{
                                            width: 24,
                                            height: 24,
                                            color: "black",
                                          }}
                                        />
                                      </Link>
                                    </TableCell>
                                    <TableCell
                                      size="small"
                                      scope="row"
                                      align="center"
                                      className="tableCell"
                                      sx={{ paddingRight: "16px !important" }}
                                    >
                                      <Link
                                        onClick={() =>
                                          handleDeferModalOpen(immunization.id)
                                        }
                                      >
                                        <img
                                          src={images.immunization_calendar}
                                          alt="DeferIcon"
                                          className="action-icon-image"
                                          style={{ width: 24, height: 24 }}
                                        />
                                      </Link>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </>
                            ) : (
                              afterFetch.current && <p>No users found.</p>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {pageCount > 0 && (
                        <Pagination
                          count={pageCount}
                          page={currentPage}
                          onChange={(_event: React.ChangeEvent, page: number) =>
                            setCurrentPage(page)
                          }
                          sx={{
                            margin: "0 auto",
                            paddingTop: "16px",
                            marginBottom: "10px",
                          }}
                          boundaryCount={3}
                        />
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ImmunizationDefer
        immunization_id={deferSelectionImId}
        modalOpen={deferModalOpen}
        setModalOpen={setDeferModalOpen}
        setRenderingKey={setRenderingKey}
      />
      <ImmunizationGiven
        immunization_id={givenSelectionImId}
        modalOpen={givenModalOpen}
        setModalOpen={setGivenModalOpen}
        setRenderingKey={setRenderingKey}
      />
    </div>
  );
};

export default Immunization;
